// react
import { ChangeEvent, FC, MouseEvent, useState, WheelEvent } from 'react';

// types
import { PaletteBrand } from '../../../@types';

// mui
import { Box, BoxProps, Collapse, Paper, SxProps, Typography } from '@mui/material';
import {
	DataGrid,
	DataGridProps,
	GridColDef,
	GridRenderCellParams,
	GridRowsProp,
	GridValidRowModel,
} from '@mui/x-data-grid';

// icons
import { ExpandMore as IconExpandMore, InfoOutlined as IconInfo } from '@mui/icons-material';

// theme
import { theme } from '../../../theme';

// utils
import { formatNumber } from '../../../utils/format';

// props
export interface OnClickCellParams {
	e?: MouseEvent<HTMLDivElement>;
	params: GridRenderCellParams;
}
export interface TableSheetProps {
	columns: GridColDef[];
	DataGridProps?: Omit<DataGridProps, 'rows' | 'columns'>;
	defaultExpanded?: boolean;
	headerAction?: React.ReactNode;
	isExpandable?: boolean;
	isStriped?: boolean;
	label: string;
	onClickCell?: ({ e, params }: OnClickCellParams) => void;
	onDataChange?: (row: GridValidRowModel, field: string, value: number | undefined) => void;
	palette?: PaletteBrand;
	rows: GridRowsProp;
}

export const TableSheet: FC<TableSheetProps> = ({
	columns,
	DataGridProps,
	defaultExpanded = false,
	headerAction,
	isExpandable = true,
	isStriped = false,
	label = 'Table',
	onClickCell,
	onDataChange,
	palette = 'green',
	rows,
}) => {
	// state
	const [isExpanded, setIsExpanded] = useState(!isExpandable ? true : defaultExpanded);

	// vars
	const colorPalette = theme.palette.brand[palette as PaletteBrand];

	// Handle row expansion
	const handleToggleExpand = () => {
		setIsExpanded(!isExpanded);
	};

	const sxBoxDefault: SxProps = {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '100%',
		height: '100%',
		padding: theme.spacing(1.5, 2),
		color: theme.palette.brand.grey[800],
	};

	const handleChange = (event: ChangeEvent<HTMLInputElement>, params: GridRenderCellParams) => {
		const value = event.target.value;

		// Remove formatting to get raw numeric string
		const rawValue = value.replace(/[^0-9.-]/g, '');

		const numericValue = rawValue === '' ? 0 : parseFloat(rawValue);
		if (onDataChange) {
			onDataChange(params.row, params.field, numericValue);
		}
	};

	// Render cell with proper styling
	const renderCell = (params: GridRenderCellParams) => {
		const isTotalField = params.field === 'total';

		// define row features
		const isClickable = params.row.fieldsClickable?.includes(params.field) && onClickCell;
		const isEditable = params.row.fieldsEditable?.includes(params.field);

		// define sticky left fields
		const isStickyLeft = (params.row.fieldsSticky?.left || []).includes(params.field);
		const isStickyRight = (params.row.fieldsSticky?.right || []).includes(params.field);

		let stickyPosition = 'none';

		if (isStickyLeft) {
			stickyPosition = 'left';
		} else if (isStickyRight) {
			stickyPosition = 'right';
		}

		// define box props
		const BoxPropsRow = { ...(params.row.BoxProps?.[params.field] || {}) };
		const { sx: sxBoxRow = {}, ...boxProps } = BoxPropsRow;
		const BoxProps: BoxProps = {
			...boxProps,
		};

		BoxProps.sx = {
			...sxBoxDefault,
		};

		// Apply striping logic - exclude total rows and total field only
		let backgroundColor =
			Number(params.id) % 2 === 0 ? theme.palette.brand.grey[100] : theme.palette.common.white;

		BoxProps.sx = {
			...BoxProps.sx,
			backgroundColor,
		};

		if (isStriped && !isTotalField) {
			// Get row index from the rows array to determine striping
			const rowIndex = rows.findIndex((row) => row.id === params.id);
			if (rowIndex !== -1 && rowIndex > 0 && rowIndex % 2 === 0) {
				backgroundColor = theme.palette.brand.grey[100];
				// Override any background color from BoxProps when striping
				BoxProps.sx = {
					...BoxProps.sx,
					backgroundColor,
				};
			}
		}

		BoxProps.sx = {
			...BoxProps.sx,
			...sxBoxRow,
		};

		// Data row - check if editable
		if (isEditable) {
			const idCell = `cell-input-${params.field}-${String(params.id)}`;

			// Format value with commas for display
			const rawVal = params.value;
			const displayValue =
				rawVal === 0 ? '0' : rawVal != null && rawVal !== '' ? Number(rawVal).toLocaleString() : '';

			return (
				<Box
					data-sticky={stickyPosition}
					{...BoxProps}
					sx={{
						...BoxProps.sx,
						padding: 0,
						// Subtle editable indicator: faint inner-bottom border
						boxShadow: `inset 0 -1px 0 0 ${theme.palette.brand[palette][200]}`,
					}}>
					<Box
						component="input"
						id={idCell}
						onWheel={(e: WheelEvent<HTMLDivElement>) => {
							const target = e.target as HTMLDivElement;
							return target.blur();
						}}
						onChange={(e) => handleChange(e as ChangeEvent<HTMLInputElement>, params)}
						onFocus={(e) => {
							const input = e.target as HTMLInputElement;
							// Show raw number on focus for easier editing
							const raw = input.value.replace(/,/g, '');
							input.value = raw;
							if (raw === '0') {
								input.select();
							}
						}}
						onBlur={(e) => {
							// Re-format with commas on blur
							const input = e.target as HTMLInputElement;
							const raw = input.value.replace(/[^0-9.-]/g, '');
							const num = raw === '' ? 0 : parseFloat(raw);
							input.value = num === 0 ? '0' : num.toLocaleString();
						}}
						placeholder="-"
						sx={{
							width: '100%',
							height: '100%',
							border: 'none',
							background: 'transparent',
							fontSize: 14,
							fontWeight: 600,
							textAlign: 'right',
							padding: theme.spacing(1.5, 2),
							color: theme.palette.brand.grey[800],
							outline: 'none',
							boxSizing: 'border-box',
							'&:hover': {
								border: `1px solid ${theme.palette.brand[palette][500]}`,
								cursor: 'pointer',
							},
							'&:focus': {
								backgroundColor: theme.palette.common.white,
								border: `1px solid ${theme.palette.brand[palette][500]}`,
							},
						}}
						type="text"
						inputMode="numeric"
						value={displayValue}
					/>
				</Box>
			);
		}

		if (isClickable) {
			BoxProps.onClick = (e) => {
				onClickCell?.({ e, params });
			};

			BoxProps.sx = {
				...BoxProps.sx,
				cursor: 'pointer',
				justifyContent: 'space-between',
				'&:hover': {
					backgroundColor: theme.palette.brand.grey[200],
				},
			};
		}

		let value = '-';

		if (params.value === null) {
			value = '';
		} else if (params.value) {
			if (typeof params.value === 'string') {
				value = params.value;
			} else {
				value = formatNumber(params.value);
			}
		}

		// Regular data cell styling
		return (
			<Box data-sticky={stickyPosition} {...BoxProps}>
				{isClickable && <IconInfo sx={{ color: theme.palette.brand.grey[500], mr: 0.5 }} />}
				<Typography
					component="span"
					fontWeight={600}
					sx={{
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: {
							lg: typeof params.value === 'number' ? 'nowrap' : 'normal',
						},
					}}>
					{value}
					{params.value !== undefined && params.row.fieldsSuffix?.[params.field]}
				</Typography>
			</Box>
		);
	};

	// Update columns with renderCell functions
	const columnsEnhanced: GridColDef[] = columns.map((col) => {
		return {
			...col,
			renderCell,
		};
	});

	const BoxProps: BoxProps = {};

	if (isExpandable) {
		BoxProps.onClick = handleToggleExpand;
	}

	return (
		<Paper
			sx={{
				width: '100%',
				borderColor: colorPalette[400],
				borderRadius: 2,
				borderStyle: 'solid',
				borderWidth: 1,
				overflow: 'hidden',
			}}>
			<Box
				sx={() => {
					let sx: SxProps = {
						backgroundColor: colorPalette[200],
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						px: 2,
						py: 1,
						cursor: 'pointer',
					};

					if (isExpanded) {
						sx = {
							...sx,
							borderBottomColor: colorPalette[400],
							borderBottomStyle: 'solid',
							borderBottomWidth: 0.5,
						};
					}

					return sx;
				}}
				{...BoxProps}>
				<Typography sx={{ color: theme.palette.brand[palette][800] }} variant="h3">
					{label}
				</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
					{headerAction}
					{isExpandable && (
						<IconExpandMore
							sx={{
								color: theme.palette.brand[palette][800],
								transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
								transition: 'transform 0.3s ease',
							}}
						/>
					)}
				</Box>
			</Box>
			<Collapse in={isExpanded}>
				<Box sx={{ width: '100%', overflowX: 'auto' }}>
					<DataGrid
						columnHeaderHeight={0}
						columns={columnsEnhanced}
						hideFooter
						disableColumnMenu
						disableRowSelectionOnClick
						getRowHeight={() => 'auto'}
						rows={rows}
						sx={{
							border: 'none',
							width: 'fit-content',
							minWidth: '100%',
							'& .MuiDataGrid-main': {
								overflow: 'visible',
							},
							'& .MuiDataGrid-virtualScroller': {
								overflow: 'visible',
							},
							'& .MuiDataGrid-virtualScrollerContent': {
								position: 'relative',
								width: 'fit-content !important',
								minWidth: '100%',
							},
							'& .MuiDataGrid-virtualScrollerRenderZone': {
								position: 'relative',
								width: 'fit-content !important',
							},
							'& .MuiDataGrid-columnHeaders': {
								display: 'none',
							},
							'& .MuiDataGrid-cell': {
								borderColor: colorPalette[400],
								borderStyle: 'solid',
								borderWidth: 0.5,
								borderTop: 'none',
								borderLeft: 'none',
								padding: 0,
								'&:focus': {
									outline: 'none',
								},
							},
							'& .MuiDataGrid-cell:has([data-sticky="left"])': {
								position: 'sticky',
								left: 0,
								zIndex: 1000,
								backgroundColor: 'var(--sticky-left-bg, #fff)',
								boxShadow: '2px 0 4px rgba(0, 0, 0, 0.08)',
								willChange: 'transform',
								backfaceVisibility: 'hidden',
							},
							'& .MuiDataGrid-cell:has([data-sticky="right"])': {
								position: 'sticky',
								right: 0,
								zIndex: 1000,
								boxShadow: '-2px 0 0 rgba(0, 0, 0, 0.1)',
								willChange: 'transform',
								backfaceVisibility: 'hidden',
								backgroundColor: colorPalette[200],
							},
							'& .MuiDataGrid-row': {
								'&:first-of-type': {
									'& .MuiDataGrid-cell': {
										borderTop: 'none',
									},
									'& .MuiDataGrid-cell:has([data-sticky="left"])': {
										zIndex: 1001,
									},
									'& .MuiDataGrid-cell:has([data-sticky="right"])': {
										zIndex: 1001,
									},
								},
								'&:last-child': {
									'& .MuiDataGrid-cell': {
										borderBottom: 'none',
									},
									'& .MuiDataGrid-cell:has([data-sticky="left"])': {
										zIndex: 1001,
									},
									'& .MuiDataGrid-cell:has([data-sticky="right"])': {
										zIndex: 1001,
									},
								},
								'&:not(:first-of-type):not(:last-child)': {
									'&:hover': {
										'& .MuiDataGrid-cell:has([data-sticky="left"])': {
											zIndex: 1001,
										},
									},
								},
							},
							'& .MuiDataGrid-cell:first-of-type': {
								borderLeft: 'none',
							},
							'& .MuiDataGrid-cell:last-child': {
								borderRight: 'none',
							},
						}}
						{...DataGridProps}
					/>
				</Box>
			</Collapse>
		</Paper>
	);
};
